import React, { useEffect, useState } from "react";
import { ShareLink, createShareLink, listShareLinks, revokeShareLink, shareLinkURL } from "../api";

// Settings → Share your watchlist (#3213). Each link is a read-only page for one
// partner showing address, sale date, opening bid, status and photo for every
// watched property, and nothing else: no owner, assessed value, equity or
// mailing address. The link is a secret shown once (the server keeps only its
// hash); revoking it makes the page 404 immediately.

function formatDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const WatchlistShareSettings: React.FC = () => {
  const [links, setLinks] = useState<ShareLink[] | null>(null);
  const [label, setLabel] = useState("");
  const [newUrl, setNewUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listShareLinks()
      .then((l) => !cancelled && setLinks(l))
      .catch(() => !cancelled && setError("Couldn't load your share links."));
    return () => {
      cancelled = true;
    };
  }, []);

  const create = async () => {
    setBusy(true);
    setError(null);
    setCopied(false);
    try {
      const { token, link } = await createShareLink(label.trim());
      setNewUrl(shareLinkURL(token));
      setLinks((prev) => [link, ...(prev ?? [])]);
      setLabel("");
    } catch (err: any) {
      setError(
        err?.response?.data?.error === "share_link_limit"
          ? "You have the maximum number of active links. Turn one off first."
          : "Couldn't create the link. Please try again."
      );
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      await revokeShareLink(id);
      setLinks((prev) => (prev ?? []).filter((l) => l.id !== id));
    } catch {
      setError("Couldn't turn that link off. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!newUrl) return;
    try {
      await navigator.clipboard.writeText(newUrl);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <>
      <h3>Share your watchlist</h3>
      <p className="settings-section-note">
        Send a partner a read-only link to your watched properties. They see the address, sale date, opening
        bid, status and photo, with no account needed. Owner, assessed value, equity and mailing address are
        never included. Turn a link off any time.
      </p>

      {error && <p className="settings-section-note share-links-error" role="alert">{error}</p>}

      {newUrl && (
        <div className="share-links-new">
          <div className="calendar-feed-url">
            <input readOnly value={newUrl} aria-label="Watchlist share link" onFocus={(e) => e.target.select()} />
            <button type="button" className="settings-btn" onClick={copy}>
              {copied ? "Copied" : "Copy link"}
            </button>
          </div>
          <p className="settings-section-note">
            Anyone with this link can see your watched properties. This is the only time it's shown.
          </p>
        </div>
      )}

      {links && links.length > 0 && (
        <ul className="share-links-list">
          {links.map((l) => (
            <li key={l.id}>
              <span className="share-links-name">{l.label || "Unnamed link"}</span>
              <span className="share-links-meta">
                created {formatDate(l.createdAt)} · {l.viewCount} {l.viewCount === 1 ? "view" : "views"}
                {l.lastViewedAt ? `, last ${formatDate(l.lastViewedAt)}` : ""}
              </span>
              <button
                type="button"
                className="settings-link-btn"
                onClick={() => revoke(l.id)}
                disabled={busy}
                aria-label={`Turn off ${l.label || "unnamed link"}`}
              >
                Turn off
              </button>
            </li>
          ))}
        </ul>
      )}

      {links && (
        <div className="share-links-create">
          <input
            type="text"
            value={label}
            maxLength={80}
            placeholder="Who is it for? (optional)"
            aria-label="Link name"
            onChange={(e) => setLabel(e.target.value)}
          />
          <button type="button" className="settings-btn" onClick={create} disabled={busy}>
            Create share link
          </button>
        </div>
      )}
    </>
  );
};

export default WatchlistShareSettings;
